import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS, type GlobalSearchResult } from '@nest-recruit/shared';
import { request, type ApiError } from '../lib/api-client';

/**
 * F20.04 inside the palette (S25 Q2). The palette still opens with no network call (06-qol §1:
 * under 100ms) — the server is asked only once someone has typed two characters and paused
 * for 250ms (§L10). The answer is five of each kind the viewer may open, already filtered.
 */
export function usePaletteSearch(query: string): { readonly result: GlobalSearchResult | null; readonly isSearching: boolean; readonly term: string } {
  const [term, setTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setTerm(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const isEnabled = term.length >= SEARCH_MIN_CHARS;
  const search = useQuery<GlobalSearchResult, ApiError>({
    queryKey: ['search', term, 'all', 1, false],
    queryFn: ({ signal }) => request<GlobalSearchResult>('/search', { query: { q: term }, signal }).then((result) => result.data),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });

  return {
    result: isEnabled ? (search.data ?? null) : null,
    isSearching: isEnabled && search.isFetching,
    term,
  };
}
